.tableHeader {
  display: flex;
  position: sticky;
  top: 0;
  z-index: 104;
  min-width: 100%;
}

.headerRow {
  position: relative;
  display: flex;
  background-color: var(--color-surfaces-app-background-0);
  flex: 1;
  height: 4.8rem;
}

.day-name {
  font-weight: 400;
  color: var(--color-texts-high-contrast);
  line-height: 2.4rem;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.headerCell {
  display: flex;
  color: var(--color-grayscale-light-slate);
  background: transparent;
  padding: 0.8rem 0;
  font-size: 1.4rem;
  font-weight: 400;
  align-items: center;
  position: relative;
  cursor: grab;
  user-select: none;
  padding-inline-start: 1.2rem;

  &:first-child {
    margin-left: 3.2rem;
  }

  > span {
    white-space: nowrap;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.header-action {
  position: sticky;
  right: 1.6rem;
  z-index: 2;
  transform: translateY(0.8rem);
}

.resizerContainer {
  position: absolute;
  top: 0;
  height: 100%;
  width: 2.4rem;
  background: transparent;
  cursor: url('/icons/column-spacing.svg') 8 8, col-resize;
  user-select: none;
  touch-action: none;
  right: 0;
  z-index: 10;
  display: flex;
  justify-content: center;
  transform: translateX(50%);

  &:hover > .resizer {
    background: var(--color-actions-main-action);
  }
}

.resizer {
  width: 0.1rem;
  height: 100%;
  background-color: var(--color-surfaces-bg-elevation-2);
}

.resizer.isResizing {
  background: var(--color-actions-main-action);
  height: calc(100vh - 12rem);
  opacity: 1;
}

.singleTable {
  min-width: 100%;
  border-radius: 0.4rem;
  border: 0.1rem solid var(--color-surfaces-bg-elevation-2);
}

.table-body {
  min-height: 10.4rem;
  display: flex;
  flex-direction: column;
}

.stickyStrip {
  height: 4.8rem;
  position: sticky;
  left: 0;
  width: calc(100vw - 29.8rem);
  max-width: 100%;
  z-index: 101;
  padding: 0.8rem 1.2rem;
  border-radius: 0.4rem 0.4rem 0 0;
  background-color: var(--color-surfaces-bg-elevation-2);
  top: 4.8rem;

  &[data-direction='top'] {
    z-index: 102;
    top: 4.8rem;
    background-color: var(--color-primary-sandal);
    border-radius: 0.4rem 0.4rem 0 0;
  }

  &[data-direction='bottom'] {
    z-index: 103;
    top: unset;
    background-color: var(--color-shades-hibiscus);
    border-radius: 0 0 0.4rem 0.4rem;
    position: sticky;
  }

  &[data-variant='add-shot'] {
    background-color: transparent;
    border: none;
  }

  &[data-variant='empty-placeholder'] {
    background-color: transparent;
    border: none;
    height: auto;
    flex: 1;
    display: flex;
    align-items: center;

    & > div {
      background-image: none;
      min-height: unset;
    }
  }
}

.stickyBanner {
  height: 4.8rem;
  position: sticky;
  left: 0;
  max-width: 100%;
  z-index: 2;
  padding: 0;
  background-color: transparent;
}

.stickyBannerContent {
  position: absolute;
  top: 0;
  left: 0;
  width: calc(100vw - 29.8rem);
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  pointer-events: none;
}

.stickyStripContent {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.8rem 1.6rem;
}

.addShotStrip {
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  border-radius: 0.4rem;
  font-size: 1.6rem;
  color: var(--color-texts-low-contrast);
  border: 0.1rem dashed currentColor;
  user-select: none;

  &:hover {
    cursor: pointer;
    color: var(--color-texts-middle-contrast);
  }
}

.shotStrip,
.bannerStrip {
  display: flex;
  flex-direction: row;
  border: 0.1rem solid transparent;
  border-bottom: 0.1rem solid var(--color-surfaces-bg-elevation-2);
  position: relative;
  opacity: 1;

  .actionButton {
    opacity: 0;
    transition: opacity 0.2s ease;
    position: sticky;
    margin-left: auto;
    right: 1.6rem;
    padding: 0.8rem 0;
    z-index: 1;
  }

  &:hover .actionButton {
    opacity: 1;
  }

  &.shotStrip {
    min-height: 5.6rem;
    background: var(--color-surfaces-bg-elevation-1);
  }

  &.bannerStrip {
    min-height: 4.8rem;
    border-bottom: 0.1rem solid var(--color-surfaces-bg-elevation-3);
    background: var(--color-surfaces-bg-elevation-2);

    &:hover {
      background: var(--color-surfaces-bg-elevation-3);
    }

    .cell {
      border-left: none;
    }
  }

  &.selected {
    & + .selected {
      border-top: 0.1rem solid transparent;
    }

    // &:has(+ &.selected) {
    //   border-bottom: 0.1rem solid transparent;
    // }

    border: 0.1rem solid var(--color-actions-main-action);
  }

  &[data-isHidden='true'] {
    *:not(button),
    *:hover:not(button),
    *:focus-visible:not(button) {
      color: var(--color-grayscale-trout);
    }
  }

  &[data-dragged='true'] {
    border: 0.15rem dashed var(--color-primary-crayola);
    background-color: transparent !important;
    cursor: grabbing;
    z-index: 1;

    & > * {
      display: none;
    }
  }
}

.actionButton {
  display: flex;
  align-items: center;
  position: sticky;
  right: 1.6rem;
  width: fit-content;
}

.cell {
  display: flex;
  flex-direction: row;
  align-items: center;
  user-select: none;
  max-width: 100%;
  overflow: hidden;
  cursor: pointer;

  &[data-disabled='true'] {
    cursor: initial;
  }

  &:not(:first-of-type) {
    border-left: 1px solid var(--content-border-color);
  }

  &:hover {
    background-color: var(--content-border-color);
  }

  &:not(:last-child) > .shotCell {
    justify-content: flex-start;
    flex: 1;
  }

  &:first-of-type {
  }

  &:last-child {
    display: flex;
    justify-content: flex-end;
    flex: 1;
    pointer-events: none;
    position: sticky;
    right: 0;

    & > * {
      pointer-events: all;
    }
  }
}

.dragHandle {
  position: sticky;
  display: flex;
  justify-content: center;
  align-items: center;
  left: 0;
  cursor: grab;
  z-index: 100;
  width: 3.2rem;
  background: var(--content-bg-color);

  &:active {
    cursor: grabbing;
  }

  & svg {
    width: 2.8rem;
    height: 2.8rem;
  }
}

.shotCell {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 100%;
  max-width: 100%;
  padding-left: 1.2rem;
  color: #e3e4e7;
  font-size: 1.4rem;

  span {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &[data-no-padding='true'] {
    padding-left: 0;
  }
}

.cover {
  position: relative;
  width: 100%;
  max-width: 100%;
  aspect-ratio: 16 / 9;

  & svg {
    height: 1.2rem;
    width: 1.2rem;
  }
}

.locationCell {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 100%;
  max-width: 100%;
  padding: 0 !important;

  .trigger {
    width: fit-content;
  }
}

.tableWrapper {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: calc(100% - 1.6rem);
  overflow-x: auto;

  &[data-scroll='true'] {
    max-height: calc(100vh - 13.6rem);
    overflow-y: auto;
  }
}

.addShotButton {
  position: sticky;
  right: 1.6rem;
}

.overlayWrapper {
  position: absolute;
  width: 100%;
  display: flex;
  justify-content: center;
}

.drop-target {
  width: 100%;
  color: var(--color-texts-high-contrast);
  position: absolute;
  top: 0.1rem;
  left: 0.1rem;
  border: 0.1rem dashed var(--color-actions-main-action);
  border-radius: 0.4rem;
  min-height: 5.6rem;
}
